// Typed fetch client for the rems-crm API (platform cmms/itms-qc pattern).
//
// API base resolution:
//   - Deployed: VITE_API_BASE_URL is baked per env at BUILD time and MUST
//     include the /api suffix (e.g. https://app-rems-crm-api-dev.azurewebsites.net/api).
//     The SPA calls the API cross-origin with the user's Entra bearer; the
//     API validates it directly (no portal-side proxy).
//   - Local dev: unset → '/api', served by the Vite dev proxy → :4000.
//
// Every method takes the access token as its first argument — acquire it
// with useApiToken() (auth/useApiToken.ts).

import type { Contact, Dataset, FinancialPeriod } from '@/types/entities';
import type { DeliveryReport, TraceStatus } from '@/pages/events/delivery-types';

export const API_BASE = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function http<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  } catch (err) {
    // Network failure / cold start — status 0 marks it transient.
    throw new ApiError(0, err instanceof Error ? err.message : 'Network error');
  }
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* non-JSON error body — keep the status text */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Token provider for non-React callers (the zustand store's persistence).
// DataGate registers the MSAL getter at mount, before any user interaction
// can trigger a write — module-level state on purpose (React effect ordering
// must not gate the first write; see the platform init-order rule).
// ---------------------------------------------------------------------------
let tokenProvider: (() => Promise<string>) | null = null;
export function setTokenProvider(fn: () => Promise<string>): void {
  tokenProvider = fn;
}
async function withToken<T>(run: (token: string) => Promise<T>): Promise<T> {
  if (!tokenProvider) throw new ApiError(0, 'Not signed in yet');
  return run(await tokenProvider());
}

/** Re-fetch the whole dataset outside React — the background/manual refresh
 *  path (store/refreshDataset.ts). The bootstrap fetch still goes through
 *  api.getDataset with a token DataGate already holds. */
export const datasetApi = {
  refresh: () => withToken((t) => http<Dataset>(t, '/crm/dataset')),
};

/** Durable writes for in-app edits (crm.app_entities). */
export const persistApi = {
  create: (collection: string, entity: { id: string }) =>
    withToken((t) => http<{ ok: boolean }>(t, `/crm/entities/${collection}`, {
      method: 'POST', body: JSON.stringify(entity),
    })),
  patch: (collection: string, id: string, partial: object) =>
    withToken((t) => http<{ ok: boolean }>(t, `/crm/entities/${collection}/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(partial),
    })),
  remove: (collection: string, id: string) =>
    withToken((t) => http<{ ok: boolean }>(t, `/crm/entities/${collection}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })),
};

export interface FreshnessFeed {
  syncType: string;
  /** Stamped on failed runs too — never read it alone as "synced". */
  lastRunAt: string | null;
  lastStatus: string | null;
  /** The last run failed for a reason retrying cannot fix (bad source
   *  object/column/grant). */
  structurallyBroken: boolean;
  /** When the current streak of failed runs began, where the sync recorded
   *  it (financials); null when not failing or not known. */
  failingSince?: string | null;
  dataThrough: string | null;
}

/** One row of a wave's audience review. `alreadySent` is the exclusion the
 *  sender acts on; `everSent` is the raw send history. They disagree exactly
 *  when `addressChanged` — mailed before, but at an address corrected since,
 *  so that copy never reached the person (2026-09-09). */
export interface WaveAudienceRow {
  id: string;
  contactId: string;
  firstName: string;
  email: string | null;
  rsvp: string;
  tier: number | null;
  alreadySent: boolean;
  everSent: boolean;
  addressChanged: boolean;
  mailedTo: string | null;
  bounced: boolean;
  excluded: 'manual' | 'rsvp' | 'already_sent' | 'no_email' | null;
}

export interface WavePlanResponse {
  total: number;
  toSend: number;
  alreadySent: number;
  noEmail: number;
  excludedRsvp: number;
  excludedManually: number;
  resends: number;
  /** Sendable only because the address was corrected after the last send. */
  correctedAddress: number;
  audience: WaveAudienceRow[];
  inFlight: string | null;
  emailEnabled: boolean;
}

export const api = {
  /** Full Dataset bootstrap — everything the store hydrates from. */
  getDataset: (token: string) => http<Dataset>(token, '/crm/dataset'),

  /** Caller identity + wise-iam permissions (UI gating only). */
  me: (token: string) =>
    http<{ email: string; displayName: string; permissions: string[] }>(token, '/crm/me'),

  /** Sync provenance for the rail's Data-freshness panel. */
  getFreshness: (token: string) =>
    http<{ generatedAt: string; feeds: FreshnessFeed[] }>(token, '/crm/freshness'),

  /** Investor Symposium console (crm.events.manage). Loose types on
   *  purpose — the console owns its own view models. */
  symposium: {
    listEvents: (token: string) => http<any[]>(token, '/crm/symposium/events'),
    createEvent: (token: string, body: object) =>
      http<any>(token, '/crm/symposium/events', { method: 'POST', body: JSON.stringify(body) }),
    getEvent: (token: string, id: string) => http<any>(token, `/crm/symposium/events/${id}`),
    patchEvent: (token: string, id: string, body: object) =>
      http<any>(token, `/crm/symposium/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** Raw image bytes (not JSON) — the API sniffs the real type server-side. */
    uploadPortalBackground: async (token: string, id: string, file: File) => {
      const res = await fetch(`${API_BASE}/crm/symposium/events/${id}/portal-background`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type || 'image/jpeg' },
        body: file,
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `Upload failed (${res.status})`)
      }
      return res.json() as Promise<{ ok: boolean }>
    },
    removePortalBackground: (token: string, id: string) =>
      http<{ ok: boolean }>(token, `/crm/symposium/events/${id}/portal-background`, { method: 'DELETE' }),
    listInvites: (token: string, id: string) => http<any[]>(token, `/crm/symposium/events/${id}/invites`),
    /** Bulk-invite WPI employees to ATTEND. Flags `staff_guest`, which is
     *  independent of `invite_status` — on a staff row that column means
     *  "working the event". */
    addStaffInvites: (token: string, eventId: string, contactIds: string[]) =>
      http<{ ok: boolean; created: number; flagged: number }>(
        token, `/crm/symposium/events/${eventId}/staff-invites`,
        { method: 'POST', body: JSON.stringify({ contactIds }) }),

    addInvite: (token: string, id: string, body: object) =>
      http<any>(token, `/crm/symposium/events/${id}/invites`, { method: 'POST', body: JSON.stringify(body) }),
    patchInvite: (token: string, inviteId: string, body: object) =>
      http<any>(token, `/crm/symposium/invites/${inviteId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** Approve a self-registration: marks them invited, records the RSVP they
     *  already gave, and emails the confirmation + calendar file. Not the same
     *  as setting Invite Status to Invited by hand, which does none of that. */
    approveRegistration: (token: string, inviteId: string) =>
      http<{ ok: boolean; confirmationSent: boolean; mailDetail: string | null }>(
        token, `/crm/symposium/invites/${inviteId}/approve-registration`, { method: 'POST' }),
    /** Decline one. Deliberately silent — nobody learns they were turned down
     *  from an automated email. */
    declineRegistration: (token: string, inviteId: string) =>
      http<{ ok: boolean }>(
        token, `/crm/symposium/invites/${inviteId}/decline-registration`, { method: 'POST' }),
    previewLink: (token: string, inviteId: string) =>
      http<{ token: string; path: string }>(token, `/crm/symposium/invites/${inviteId}/preview-link`, { method: 'POST' }),
    trackingLink: (token: string, inviteId: string) =>
      http<{ token: string; path: string; url?: string }>(token, `/crm/symposium/invites/${inviteId}/tracking-link`, { method: 'POST' }),
    /** The invitation rendered exactly as it would be sent — nothing mailed. */
    previewEmail: (token: string, inviteId: string, kind?: string) =>
      http<{ subject: string; html: string; to: string | null; kind: string }>(token, `/crm/symposium/invites/${inviteId}/preview-email`, { method: 'POST', body: JSON.stringify({ kind }) }),
    sendInvite: (token: string, inviteId: string, mode: 'test' | 'real', kind?: string) =>
      // stampFailed: the mail LEFT but the invite stamp + send row could not
      // be written (audited as invite_email_stamp_failed) — never resend on it.
      http<{ ok: boolean; to: string; test: boolean; stampFailed?: boolean; sendId?: string }>(token, `/crm/symposium/invites/${inviteId}/send-invite`, { method: 'POST', body: JSON.stringify({ mode, kind }) }),
    /** Event-level template preview (Email Preview tab) — generic greeting, nothing mailed. */
    previewEventEmail: (token: string, id: string, kind: string) =>
      http<{ subject: string; html: string; kind: string }>(token, `/crm/symposium/events/${id}/preview-email`, { method: 'POST', body: JSON.stringify({ kind }) }),
    /** Send the selected template to the signed-in user ([TEST] prefix). */
    sendTestEventEmail: (token: string, id: string, kind: string) =>
      http<{ ok: boolean; to: string; kind: string }>(token, `/crm/symposium/events/${id}/send-test-email`, { method: 'POST', body: JSON.stringify({ kind }) }),
    mailboxStatus: (token: string) =>
      http<{ available: boolean; reason?: string; inboxTotal?: number; inboxUnread?: number; lastScan?: { at: string; scanned: number; ndrs: number; bounced: number; unmatched: number; error?: string } | null; trace?: TraceStatus | null }>(token, '/crm/symposium/mailbox'),
    /** Delivery report — one row per send from the crm.symposium_sends
     *  ledger with the trace sweep's verdicts. waveId = a wave id or 'all'. */
    delivery: (token: string, eventId: string, waveId: string, includeTests = false) =>
      http<DeliveryReport>(
        token, `/crm/symposium/events/${eventId}/delivery?waveId=${encodeURIComponent(waveId)}&includeTests=${includeTests ? 1 : 0}`),
    draftWave: (token: string, id: string, body: object) =>
      http<any>(token, `/crm/symposium/events/${id}/waves`, { method: 'POST', body: JSON.stringify(body) }),
    /** 202 + the plan; delivery continues server-side, poll the bundle. */
    sendWave: (token: string, waveId: string) =>
      http<{ started: boolean; total: number; toSend: number; alreadySent: number; noEmail: number }>(
        token, `/crm/symposium/waves/${waveId}/send`, { method: 'POST' }),
    /** What a send WOULD do — same resolver the sender uses. */
    wavePlan: (token: string, waveId: string) =>
      http<WavePlanResponse>(token, `/crm/symposium/waves/${waveId}/plan`),
    /** Stateless plan for a filter — drives the compose screen + roster review. */
    wavePlanPreview: (token: string, id: string, audienceFilter: object) =>
      http<WavePlanResponse>(
        token, `/crm/symposium/events/${id}/wave-plan`, { method: 'POST', body: JSON.stringify({ audienceFilter }) }),
    putAgenda: (token: string, id: string, items: object[]) =>
      http<any>(token, `/crm/symposium/events/${id}/agenda`, { method: 'PUT', body: JSON.stringify(items) }),
    putPlaybook: (token: string, id: string, body: object) =>
      http<any>(token, `/crm/symposium/events/${id}/playbook`, { method: 'PUT', body: JSON.stringify(body) }),
  },

  /**
   * Email→account-history ingestion: the API summarizes the submitted
   * threads (Claude) and stores type:'Email' activities. Bodies are
   * discarded server-side. 503 = summarizer not configured for this env.
   */
  emailIngest: (
    token: string,
    body: { accountId: string; accountName?: string; threads: object[] },
  ) =>
    http<{
      results: {
        conversationId: string;
        id: string;
        status: 'created' | 'updated' | 'unchanged' | 'error';
        title: string;
        error?: string;
        activity?: object;
        /** Suggested tasks extracted from the summaries (created/updated
         *  threads only) — offered to the user, never auto-created. */
        actionItems?: { title: string; dueDate?: string; owner?: string }[];
      }[];
    }>(token, '/crm/email-ingest', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * P&L periods for one property. Requires crm.financials.view — a 403 is
   * an expected outcome for most users, not an error to surface loudly.
   */
  getFinancials: (token: string, propertyId: string) =>
    http<FinancialPeriod[]>(token, `/crm/financials?propertyId=${encodeURIComponent(propertyId)}`),

  /**
   * Turn email signature blocks into structured contact fields. The caller
   * has already stripped everything above the signature in the browser —
   * only signature text is sent, and the API stores none of it.
   */
  parseSignatures: (
    token: string,
    signatures: { email: string; displayName?: string; block: string }[],
  ) =>
    http<{ contacts: ParsedSignatureResult[] }>(token, '/crm/contact-sync/parse-signatures', {
      method: 'POST', body: JSON.stringify({ signatures }),
    }),

  /**
   * Maintenance banner + build stamp. Polled on a timer by
   * useServiceStatus — this one call is also the app's heartbeat, so its
   * failure mode is meaningful: ApiError(0) / 502 / 503 / 504 mean the API
   * is unreachable, anything else means it answered.
   */
  notice: (token: string) =>
    http<{ notice: ServiceNotice | null; build: string }>(token, '/crm/notice'),

  setNotice: (token: string, body: { message: string; startsAt?: string | null; expectedMinutes?: number | null }) =>
    http<{ ok: boolean }>(token, '/crm/notice', { method: 'PUT', body: JSON.stringify(body) }),

  clearNotice: (token: string) =>
    http<{ ok: boolean }>(token, '/crm/notice', { method: 'DELETE' }),

  /** Duplicate detection + merge (crm.manage). Detection runs SERVER-side:
   *  the store holds a snapshot, and normalised-name matching needs every
   *  row at once. */
  duplicates: (token: string) =>
    http<DuplicatesResponse>(token, '/crm/contacts/duplicates'),

  /** "Not the same person": every pair among `ids` is remembered as two
   *  different people, so the review stops asking (same gate as merge). */
  dismissDuplicates: (token: string, ids: string[]) =>
    http<{
      ok: boolean; pairs: number; recorded: number; alreadyRecorded: number; dismissedCount: number
      /** The pairs this call wrote — what an Undo takes back, and nothing more. */
      newPairs: Array<[string, string]>
    }>(token, '/crm/contacts/duplicates/dismiss', { method: 'POST', body: JSON.stringify({ ids }) }),

  /** Undo of the above — forgets every pair among `ids` (send one recorded
   *  pair at a time), and the group comes back. */
  undismissDuplicates: (token: string, ids: string[]) =>
    http<{ ok: boolean; removed: number; dismissedCount: number }>(
      token, '/crm/contacts/duplicates/undismiss', { method: 'POST', body: JSON.stringify({ ids }) }),

  /** `fieldChoices` decides contested fields one by one; omit it and the
   *  server applies the old fill-the-blanks rule unchanged. */
  mergeContacts: (
    token: string, survivorId: string, loserId: string,
    mode: 'duplicate' | 'spouse' = 'duplicate',
    fieldChoices?: Record<string, FieldChoice>,
  ) =>
    http<MergeResult>(token, '/crm/contacts/merge', {
      method: 'POST',
      body: JSON.stringify({ survivorId, loserId, mode, ...(fieldChoices ? { fieldChoices } : {}) }),
    }),

  /** Record a spouse on one contact. `absorb` folds the spouse's own record
   *  in as well — opt-in, never the default. */
  setSpouse: (token: string, contactId: string, body: {
    spouseName?: string; spouseContactId?: string; absorb?: boolean;
  }) =>
    http<{ ok?: boolean; spouseRecorded?: string; absorbed?: boolean }>(
      token, `/crm/contacts/${encodeURIComponent(contactId)}/spouse`,
      { method: 'POST', body: JSON.stringify(body) }),

  /** The data-corrections queue. Reading and reporting are baseline crm.use —
   *  the people who notice a wrong number are the ones using it. Applying and
   *  dismissing are crm.manage, enforced server-side. */
  dataSuggestions: (token: string) =>
    http<{ suggestions: DataSuggestion[] }>(token, '/crm/data-quality/suggestions'),

  reportDataIssue: (token: string, body: { entityId: string; note: string; field?: string; suggestedValue?: string }) =>
    http<{ ok: boolean; id: string }>(token, '/crm/data-quality/report', {
      method: 'POST', body: JSON.stringify(body),
    }),

  applyDataSuggestion: (token: string, s: DataSuggestion, value: string) =>
    http<{ ok: boolean }>(token, '/crm/data-quality/apply', {
      method: 'POST',
      body: JSON.stringify({
        key: s.key, entityId: s.entityId, issueKind: s.issueKind,
        field: s.field, currentValue: s.currentValue, value,
      }),
    }),

  dismissDataSuggestion: (token: string, s: DataSuggestion, note?: string) =>
    http<{ ok: boolean }>(token, '/crm/data-quality/dismiss', {
      method: 'POST',
      body: JSON.stringify({
        key: s.key, entityId: s.entityId, issueKind: s.issueKind,
        field: s.field, currentValue: s.currentValue, note,
      }),
    }),
};

export interface DataSuggestion {
  key: string;
  entityType: 'contact';
  entityId: string;
  entityLabel: string;
  issueKind: string;
  title: string;
  detail: string;
  /** fix = applyable here · review = needs a person · gap = missing data */
  severity: 'fix' | 'review' | 'gap';
  field?: string;
  currentValue?: string;
  /** Present only when the change is unambiguous. */
  suggestedValue?: string;
}

/** What a candidate record is attached to. The reason it is here: the row
 *  with the emptier card is regularly the one holding the invitations, so
 *  "which looks more complete" was never a safe way to pick a survivor. */
export interface DuplicateAttachments {
  invites: number;
  /** Invitations actually mailed from this record. */
  sends: number;
  /** Tasks, notes, activities and other document-store rows pointing at it. */
  refs: number;
  accountName: string | null;
}

export interface DuplicateContact {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  workEmail: string | null;
  personalEmail: string | null;
  accountId: string | null;
  isWpi: boolean;
  createdAt: string | null;
  createdBy: string | null;
  /** The COMPLETE contact, so the review screen can show every field rather
   *  than the handful this envelope happens to name. */
  record: Contact | null;
  attachments: DuplicateAttachments;
}

/** A field the reviewer may decide, served by the API so the list lives in
 *  one place (services/contact-merge.ts). */
export interface MergeableField {
  key: string;
  label: string;
}

/** Per-field decision. Absent = the default fill-the-blanks rule. */
export type FieldChoice = 'survivor' | 'loser';

export interface DuplicateGroup {
  key: string;
  /** 'name' — same person, two ids; 'email' — same address on two rows. */
  reason: 'name' | 'email' | string;
  contacts: DuplicateContact[];
  /** Server's verdict on whether these are one person. 'high' means the rows
   *  actively disagree (different addresses or accounts) — the UI withholds
   *  the default answer rather than blocking. */
  risk: 'low' | 'high';
  riskReason: string | null;
  /** Pairs inside this group already marked "not the same person". Only a
   *  group of three or more can show with one — a two-record group whose one
   *  pair is decided is not served at all. */
  dismissedPairs?: Array<[string, string]>;
}

export interface DuplicatesResponse {
  groups: DuplicateGroup[];
  fields: MergeableField[];
  /** Pairs marked "not the same person" (both records still exist). Optional
   *  only so a portal that reaches an older API reads it as 0. */
  dismissedCount?: number;
  /** Candidate groups left out this time because every pair was decided. */
  hiddenGroups?: number;
}

export interface MergeResult {
  survivorId: string;
  loserId: string;
  mode?: 'duplicate' | 'spouse';
  spouseRecorded?: string;
  fieldsFilled: string[];
  /** Fields where the other row's value was chosen over one the survivor
   *  already had — only possible when fieldChoices were sent. */
  fieldsOverwritten?: string[];
  invitesRepointed: number;
  invitesDiscarded: number;
  symposiumYearsMoved: number;
  entityRefsRepointed: number;
  accountsRepointed: number;
  mergeId: string | null;
}

export interface ParsedSignatureResult {
  email: string;
  firstName: string;
  lastName: string;
  title: string;
  company: string;
  officePhone: string;
  cellPhone: string;
}

export interface ServiceNotice {
  kind: string;
  message: string;
  startsAt: string | null;
  expectedMinutes: number | null;
  updatedBy: string | null;
  updatedAt: string | null;
}
